import React, { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AgentTool, useListScreensQuery, useUpdateToolMutation } from '@/redux/internal-services/agentsApi';
import { AgentFormValues, SectionCard, Toggle, JsonField, inputCls, areaCls, labelCls, screenMeta } from './agentShared';
import { computeToolPlacements, isUnplaced, type ToolPlacement } from './toolPlacement';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@/utils/apiError';
import { ToolErrorsForTool } from '@/components/ToolErrors/ToolErrorsForTool';

interface ToolsTabProps {
  allTools: AgentTool[];
}

// ── placement badge (shared source of truth with the Modules tab — toolPlacement.ts) ──────────

const PlacementBadge: React.FC<{ placement: ToolPlacement | undefined }> = ({ placement }) => {
  if (isUnplaced(placement)) {
    return (
      <Badge
        variant="warning"
        className="hidden sm:inline-flex"
        title="Owned but not assigned globally or to any screen — SAM won't offer it. Assign it in the Modules tab."
      >
        Not placed
      </Badge>
    );
  }
  if (placement!.isGlobal) {
    return (
      <Badge variant="accent" className="hidden sm:inline-flex" title="Enabled globally — offered to the agent every turn">
        Global
      </Badge>
    );
  }
  const labels = placement!.screens.map((m) => screenMeta(m).label);
  return (
    <Badge variant="neutral" className="hidden sm:inline-flex" title={`Enabled for: ${labels.join(', ')} — not offered on other screens`}>
      {labels.join(', ')}
    </Badge>
  );
};

// ── Enabled tool accordion row ────────────────────────────────────────────────

const EnabledToolRow: React.FC<{
  tool: AgentTool;
  placement: ToolPlacement | undefined;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onUpdate: (patch: Partial<AgentTool>) => void;
}> = ({ tool, placement, expanded, onToggleExpand, onRemove, onUpdate }) => (
  <div className="overflow-hidden rounded-control border border-ui-border">
    {/* compact header */}
    <div
      className="flex cursor-pointer select-none items-center gap-2 px-3 py-2.5 transition-colors hover:bg-ui-hover"
      onClick={onToggleExpand}
    >
      <span className="text-ui-text3 text-xs w-3 shrink-0">{expanded ? '▾' : '▸'}</span>
      <span className="font-mono text-sm text-ui-text flex-1 truncate min-w-0">
        {tool.name || tool.tool_name}
      </span>
      <div className="flex items-center gap-1.5 shrink-0">
        <PlacementBadge placement={placement} />
        {tool.toolkit_name && (
          <Badge variant="neutral" className="hidden sm:inline-flex">
            {tool.toolkit_name}
          </Badge>
        )}
        {tool.requires_confirmation && <Badge variant="warning">confirm</Badge>}
        <Button
          type="button"
          variant="destructive-ghost"
          size="xs"
          className="ml-1"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          Remove
        </Button>
      </div>
    </div>

    {/* expandable editor */}
    {expanded && (
      <div className="space-y-3 border-t border-ui-border bg-ui-subtle/50 px-3 pb-4 pt-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              Actual tool name <span className="text-ui-text3 font-normal">— fixed</span>
            </label>
            <div className={cn(inputCls, 'flex cursor-default items-center bg-ui-subtle font-mono text-ui-text2')}>
              {tool.name || tool.tool_name}
            </div>
          </div>
          <div>
            <label className={labelCls}>
              Agent name <span className="text-ui-text3 font-normal">— what this agent calls it</span>
            </label>
            <input
              className={cn(inputCls, 'font-mono')}
              value={tool.tool_name ?? ''}
              onChange={(e) => onUpdate({ tool_name: e.target.value.replace(/\s+/g, '_') })}
              placeholder={tool.name}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>
            Description <span className="text-ui-text3 font-normal">— shown to the model</span>
          </label>
          <textarea
            className={areaCls}
            rows={2}
            value={tool.description ?? ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="What this tool does and when to use it"
          />
        </div>

        <div>
          <label className={labelCls}>
            Argument schema <span className="text-ui-text3 font-normal">— JSON schema of the tool's inputs</span>
          </label>
          <JsonField
            value={tool.argument_schema ?? { type: 'object', properties: {} }}
            onChange={(v) => onUpdate({ argument_schema: v as Record<string, unknown> })}
            rows={5}
            placeholder='{"type": "object", "properties": {}}'
          />
        </div>

        <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
          <Toggle
            on={!!tool.requires_confirmation}
            onChange={() => onUpdate({ requires_confirmation: !tool.requires_confirmation })}
          />
          Require user confirmation before running
        </label>

        {tool.requires_confirmation && (
          <div>
            <label className={labelCls}>
              Confirmation card template <span className="text-ui-text3 font-normal">— optional JSON</span>
            </label>
            <JsonField
              value={tool.action_card_template ?? {}}
              onChange={(v) => onUpdate({ action_card_template: v })}
              rows={3}
              placeholder='{"title": "Confirm action", "fields": []}'
            />
          </div>
        )}

        <ToolOutputOverride tool={tool} onUpdate={onUpdate} />
        <ToolErrorsForTool toolName={tool.name || tool.tool_name || ''} />
      </div>
    )}
  </div>
);

// ── Per-agent output override (opt-in; defaults to the shared catalogue definition) ──────────

const ToolOutputOverride: React.FC<{ tool: AgentTool; onUpdate: (patch: Partial<AgentTool>) => void }> = ({
  tool, onUpdate,
}) => {
  // Overriding is "on" the moment any *_override field is non-null on this attached tool — not a
  // separate stored flag, so there's nothing to go out of sync with the fields themselves.
  const isOverriding =
    tool.output_schema_override != null ||
    tool.apply_data_template_override != null ||
    tool.apply_data_projection_override != null ||
    tool.custom_fields_override != null;

  const enableOverride = () => onUpdate({
    // Seed from the resolved (catalogue) values so the admin edits a real starting point instead
    // of a blank template — everything here still only takes effect for THIS agent.
    output_schema_override: tool.output_schema ?? {},
    apply_data_template_override: tool.apply_data_template ?? false,
    apply_data_projection_override: tool.apply_data_projection ?? false,
    custom_fields_override: tool.custom_fields ?? {},
  });

  const clearOverride = () => onUpdate({
    output_schema_override: null,
    apply_data_template_override: null,
    apply_data_projection_override: null,
    custom_fields_override: null,
  });

  return (
    <div className="border-t border-ui-border pt-3 space-y-3">
      <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
        <Toggle on={isOverriding} onChange={() => (isOverriding ? clearOverride() : enableOverride())} />
        Customize output for this agent
      </label>
      <p className="text-xs text-ui-text3 -mt-2 ml-9">
        {isOverriding
          ? 'Overriding the shared tool catalogue default — only this agent is affected.'
          : "Using the shared tool catalogue default (edited from that tool's row in the catalogue below) — every agent that attaches this tool sees the same output."}
      </p>

      {isOverriding && (
        <div className="ml-9 space-y-3">
          <div>
            <label className={labelCls}>
              Output schema override <span className="text-ui-text3 font-normal">— status_message / data, for this agent only</span>
            </label>
            <JsonField
              value={tool.output_schema_override}
              onChange={(v) => onUpdate({ output_schema_override: v as { status?: string; status_message?: string; data?: unknown } })}
              rows={4}
              placeholder='{"status": "success", "status_message": "Found {{ total }} users.", "data": {"total": 12}}'
            />
          </div>

          <p className="text-xs text-ui-text3">
            status_message in the override above renders as a Jinja template automatically whenever it's set (success only) — no separate toggle needed.
          </p>

          <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
            <Toggle
              on={!!tool.apply_data_template_override}
              onChange={() => onUpdate({ apply_data_template_override: !tool.apply_data_template_override })}
            />
            Render data as a Jinja→JSON template
          </label>

          <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
            <Toggle
              on={!!tool.apply_data_projection_override}
              onChange={() => onUpdate({ apply_data_projection_override: !tool.apply_data_projection_override })}
            />
            Filter data to schema keys only
          </label>

          <div>
            <label className={labelCls}>
              Custom fields override <span className="text-ui-text3 font-normal">— for this agent only, name → Jinja2 template</span>
            </label>
            <JsonField
              value={tool.custom_fields_override}
              onChange={(v) => onUpdate({ custom_fields_override: v as Record<string, string> })}
              rows={2}
              placeholder='{"llm_instruction": "For this agent, phrase results more casually.", "ui_note": "..."}'
            />
            <p className="text-xs text-ui-text3 mt-1">
              Not limited to llm_instruction — any number of arbitrarily-named fields, same as the catalogue's custom_fields. Each renders fresh against this call's real data and merges into the result as result[name].
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Catalogue tool row (editable: output_schema + its apply_* return-shaping flags) ────

// Jinja filters worth surfacing as one-click chips for every array field found — e.g. a `users`
// array gets `{{ users | length }}` offered alongside the raw `{{ users[0].name }}` path, so an
// admin doesn't need to already know Jinja's filter syntax to ask for a count.
const ARRAY_FILTER_SUGGESTIONS = ['length', 'first', 'last'] as const;

// Globals available in every template regardless of this tool's output_schema — not derivable
// from output_schema.data since they're not part of any tool's return value (see `now` in
// app/services/llm/jinja_renderer.py:_make_env). Always UTC.
const UTILITY_VARIABLES = [
  "now().strftime('%I:%M %p')",
  "now().strftime('%Y-%m-%d')",
] as const;

// Walks a sample `output_schema.data` value and returns:
// - `paths`: the dotted merge-variable paths it exposes (e.g. {"users": [{"name": "..."}]} ->
//   ["users[0].name"])
// - `arrayPaths`: the paths that are themselves arrays (e.g. ["users"]), used to also offer
//   filter chips like `{{ users | length }}`
// This is the single source of truth for what an admin can reference in the templates below, so
// the list can never drift out of sync with output_schema the way a hand-typed allowlist could.
function analyzeSchemaData(value: unknown, prefix = ''): { paths: string[]; arrayPaths: string[] } {
  if (value === null || value === undefined) return { paths: [], arrayPaths: [] };
  if (Array.isArray(value)) {
    const arrayPaths = prefix ? [prefix] : [];
    const first = value[0];
    if (first === undefined) return { paths: prefix ? [prefix] : [], arrayPaths };
    const nested = analyzeSchemaData(first, `${prefix}[0]`);
    return { paths: nested.paths, arrayPaths: [...arrayPaths, ...nested.arrayPaths] };
  }
  if (typeof value === 'object') {
    const paths: string[] = [];
    const arrayPaths: string[] = [];
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      const nested = analyzeSchemaData(v, prefix ? `${prefix}.${key}` : key);
      paths.push(...nested.paths);
      arrayPaths.push(...nested.arrayPaths);
    }
    return { paths, arrayPaths };
  }
  return { paths: prefix ? [prefix] : [], arrayPaths: [] };
}

// Once `apply_data_template` is turned on for a tool, output_schema.data stops being a raw sample
// object and becomes a JINJA TEMPLATE STRING instead, e.g. `"{\"clauses\": {{ clauses | tojson }}}"`
// — analyzeSchemaData can't walk a string as an object, so it would silently report zero variables
// for every tool in this (now-common) state. Recover the real variable names by pulling every
// `{{ ... }}` expression out of the string and taking its base identifier (before any `| filter`
// or `.field`/`[0]` access) — e.g. `{{ clauses | tojson }}` -> "clauses", `{{ users[0].name }}` ->
// "users". Different tools legitimately name this root variable differently (`clauses`, `views`,
// `users`, ...), which is exactly why this must be read off each tool's own template rather than
// assumed from another tool's example.
function extractTemplateVariables(templateText: string): string[] {
  const names = new Set<string>();
  for (const match of templateText.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
    const beforeFilter = match[1].split('|')[0].trim();
    const base = beforeFilter.split(/[.[]/)[0].trim();
    if (base && /^[A-Za-z_][A-Za-z0-9_]*$/.test(base)) names.add(base);
  }
  return [...names];
}

const CatalogueToolRow: React.FC<{ tool: AgentTool; added: boolean; onAdd: () => void }> = ({
  tool, added, onAdd,
}) => {
  const [expanded, setExpanded] = useState(false);
  const draftFrom = (t: AgentTool) => ({
    output_schema: t.output_schema ?? {},
    apply_data_template: t.apply_data_template ?? false,
    apply_data_projection: t.apply_data_projection ?? false,
    custom_fields: t.custom_fields ?? {},
  });
  const [draft, setDraft] = useState(draftFrom(tool));
  const [updateTool, { isLoading: saving }] = useUpdateToolMutation();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Stay in sync if the catalogue entry changes elsewhere (e.g. refetch after another admin's save).
  React.useEffect(() => {
    setDraft(draftFrom(tool));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tool.output_schema, tool.apply_data_template, tool.apply_data_projection, tool.custom_fields,
  ]);

  const availableVariables = useMemo(() => {
    const rawData = (draft.output_schema as { data?: unknown } | null)?.data;

    // apply_data_template rewrites output_schema.data into a Jinja template STRING (see
    // extractTemplateVariables docstring) — recover names from the template itself rather than
    // walking it as an object (it isn't one anymore). Unlike the raw-object case below, a name
    // parsed out of template text carries no type information — it could be a count (int), a flag
    // (bool), or a list. Offering | length/first/last on all of them regardless was actively
    // misleading (e.g. suggesting `{{ exhaustive | length }}` on a boolean), so show the bare
    // variable only here; filter suggestions stay restricted to fields we've actually confirmed
    // are arrays.
    if (typeof rawData === 'string') {
      return extractTemplateVariables(rawData);
    }

    // A dict's own keys are usable bare (e.g. {{ total }} — the long-standing convention). A
    // top-level list has no keys to unpack, so the backend instead always exposes the whole
    // value as {{ data }} (see _template_context in parse_tools.py) — start the walk from that
    // alias so the chips shown here match what actually renders.
    const isTopLevelArray = Array.isArray(rawData);
    const { paths, arrayPaths } = analyzeSchemaData(rawData, isTopLevelArray ? 'data' : '');
    const filterChips = arrayPaths.flatMap((p) => ARRAY_FILTER_SUGGESTIONS.map((f) => `${p} | ${f}`));
    return [...paths, ...filterChips];
  }, [draft.output_schema]);

  const original = draftFrom(tool);
  const dirty = JSON.stringify(draft) !== JSON.stringify(original);

  const dataTemplateFootgun =
    draft.apply_data_template &&
    typeof draft.output_schema?.data !== 'string' &&
    JSON.stringify(draft.output_schema?.data ?? null) === JSON.stringify(tool.output_schema?.data ?? null) &&
    JSON.stringify(draft.output_schema?.data ?? null).indexOf('{{') === -1;

  const handleSave = async () => {
    // ToolUpsertRequest requires `name`; Save is disabled without it (see button below).
    if (!tool.tool_id || !tool.name) return;
    // ToolUpsertRequest requires `name` even for a partial update — include it unchanged.
    setSaveError(null);
    try {
      await updateTool({ tool_id: tool.tool_id, body: { name: tool.name, ...draft } }).unwrap();
    } catch (err) {
      setSaveError(apiErrorMessage(err, t, t('agentConfig.toolSaveFailed')));
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="bg-ui-subtle/40 ">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-ui-text3 text-xs w-3 shrink-0"
        >
          {expanded ? '▾' : '▸'}
        </button>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setExpanded((e) => !e)}>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-ui-text">{tool.name || tool.tool_name}</span>
            {tool.requires_confirmation && <Badge variant="warning">confirm</Badge>}
          </div>
          {tool.description && (
            <p className="text-xs text-ui-text3 mt-0.5 line-clamp-1">{tool.description}</p>
          )}
        </div>
        {added ? (
          <span className="text-xs text-success-text font-medium shrink-0">Added</span>
        ) : (
          <Button type="button" variant="soft" size="xs" className="shrink-0" onClick={onAdd}>
            + Add
          </Button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-ui-border px-4 pb-4 pt-3 space-y-3">
          <div>
            <label className={labelCls}>
              Output schema <span className="text-ui-text3 font-normal">— status_message / data, ALWAYS shown to the LLM as documentation, and (below) also the live template source</span>
            </label>
            <JsonField
              value={draft.output_schema}
              onChange={(v) => setDraft((d) => ({ ...d, output_schema: v as { status?: string; status_message?: string; data?: unknown } }))}
              rows={4}
              placeholder='{"status": "success", "status_message": "Found {{ total }} users.", "data": {"total": 12}}'
            />
            <p className="text-xs text-ui-text3 mt-1">
              Shared by every agent that attaches this tool — an edit is live on the next call, no agent re-save needed.
            </p>
          </div>

          <p className="text-xs text-ui-text3">
            output_schema.status_message above renders as a Jinja template automatically whenever it's set — e.g. {'{{ total }}'} or {"{{ 's' if total != 1 else '' }}"} — on a SUCCESS result only; a failure/cancellation keeps the tool's own real error message untouched. No separate toggle needed: rendering plain text with no {'{{ }}'} is a harmless no-op.
          </p>

          <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
            <Toggle
              on={draft.apply_data_template}
              onChange={() => setDraft((d) => ({ ...d, apply_data_template: !d.apply_data_template }))}
            />
            Render data as a Jinja→JSON template
          </label>
          <p className="text-xs text-ui-text3 -mt-2 ml-9">
            Renders output_schema.data above as a template whose output must be valid JSON, REPLACING the real `data` — rename keys, compute/format values, reshape lists.
          </p>
          {dataTemplateFootgun && (
            <p className="-mt-2 ml-9 text-xs font-medium text-danger-text">
              ⚠ output_schema.data still looks like a static example (no {'{{ }}'} placeholders) — every call will return this exact hardcoded value instead of real data. Rewrite it into a template first.
            </p>
          )}

          <label className="flex items-center gap-2.5 text-sm text-ui-text cursor-pointer">
            <Toggle
              on={draft.apply_data_projection}
              onChange={() => setDraft((d) => ({ ...d, apply_data_projection: !d.apply_data_projection }))}
            />
            Filter data to schema keys only
          </label>
          <p className="text-xs text-ui-text3 -mt-2 ml-9">
            No template authoring: keeps only the keys output_schema.data declares (missing → null, extras dropped). Enable at most one of the two data toggles — the template above already controls shape.
          </p>

          <div>
            <label className={labelCls}>
              Available merge variables <span className="text-ui-text3 font-normal">— auto-detected from output_schema.data above, click to insert</span>
            </label>
            {availableVariables.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {availableVariables.map((path) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => setDraft((d) => {
                      // Chips insert into the conventional "llm_instruction" key for one-click
                      // convenience — the JSON editor below still accepts any other key name too.
                      const current = d.custom_fields.llm_instruction ?? '';
                      return {
                        ...d,
                        custom_fields: {
                          ...d.custom_fields,
                          llm_instruction: `${current}${current ? ' ' : ''}{{ ${path} }}`,
                        },
                      };
                    })}
                    className="rounded-chip border border-ui-border bg-ui-subtle px-1.5 py-0.5 font-mono text-xs hover:bg-ui-hover"
                    title={`Insert {{ ${path} }}`}
                  >
                    {'{{ '}{path}{' }}'}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-ui-text3">
                No sample fields yet — add keys under output_schema.data above (e.g. {'"data": {"total": 12}'}) to see them here.
              </p>
            )}
            <p className="text-xs text-ui-text3 mt-1">
              Every key path in output_schema.data is usable as {'{{ path }}'} in the templates above, plus Jinja filters (e.g. {'{{ users | length }}'}). This list is derived, not hand-maintained — it can't go stale.
            </p>
          </div>

          <div>
            <label className={labelCls}>
              Utility variables <span className="text-ui-text3 font-normal">— not from this tool's data, always available (UTC)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {UTILITY_VARIABLES.map((expr) => (
                <button
                  key={expr}
                  type="button"
                  onClick={() => setDraft((d) => {
                    const current = d.custom_fields.llm_instruction ?? '';
                    return {
                      ...d,
                      custom_fields: {
                        ...d.custom_fields,
                        llm_instruction: `${current}${current ? ' ' : ''}{{ ${expr} }}`,
                      },
                    };
                  })}
                  className="rounded-chip border border-ui-border bg-ui-subtle px-1.5 py-0.5 font-mono text-xs hover:bg-ui-hover"
                  title={`Insert {{ ${expr} }}`}
                >
                  {'{{ '}{expr}{' }}'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>
              Custom fields <span className="text-ui-text3 font-normal">— optional, name → Jinja2 template, e.g. llm_instruction for extra guidance to the model</span>
            </label>
            <JsonField
              value={draft.custom_fields}
              onChange={(v) => setDraft((d) => ({ ...d, custom_fields: v as Record<string, string> }))}
              rows={2}
              placeholder='{"llm_instruction": "If total == 0, tell the user no matches were found; do not invent a count."}'
            />
            <p className="text-xs text-ui-text3 mt-1">
              Not limited to llm_instruction — any number of arbitrarily-named fields. Each renders fresh against this call's real data on every result (success, failure, or cancelled) and merges into the result as result[name]. Blank render for a key just omits it.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!dirty || saving || !tool.tool_id || !tool.name}
              title={!tool.name ? t('agentConfig.toolMissingName') : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saved && <span className="text-xs text-success-text">Saved</span>}
            {saveError && <span className="text-xs text-danger-text">{saveError}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Catalogue toolkit group ───────────────────────────────────────────────────

const ToolkitGroup: React.FC<{
  name: string;
  tools: AgentTool[];
  enabledIds: Set<string | undefined>;
  onAdd: (t: AgentTool) => void;
  forceOpen?: boolean;
}> = ({ name, tools, enabledIds, onAdd, forceOpen }) => {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  const addedCount = tools.filter((t) => enabledIds.has(t.tool_id)).length;

  return (
    <div className="overflow-hidden rounded-control border border-ui-border">
      {/* toolkit header */}
      <div
        className="flex cursor-pointer select-none items-center gap-2 px-3 py-2.5 transition-colors hover:bg-ui-hover"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-ui-text3 text-xs w-3 shrink-0">{isOpen ? '▾' : '▸'}</span>
        <span className="text-sm font-medium text-ui-text flex-1 min-w-0 truncate">{name}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {addedCount > 0 && (
            <Badge variant="accent">
              {addedCount}/{tools.length} added
            </Badge>
          )}
          {addedCount === 0 && (
            <span className="text-xs text-ui-text3">{tools.length} tool{tools.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* tool rows inside toolkit */}
      {isOpen && (
        <div className="border-t border-ui-border divide-y divide-ui-border">
          {tools.map((t) => (
            <CatalogueToolRow
              key={t.tool_id}
              tool={t}
              added={enabledIds.has(t.tool_id)}
              onAdd={() => onAdd(t)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main ToolsTab ─────────────────────────────────────────────────────────────

const ToolsTab: React.FC<ToolsTabProps> = ({ allTools }) => {
  const { watch, setValue, getValues, control } = useFormContext<AgentFormValues>();
  const watchedTools = watch('tools');
  const selectedTools = useMemo(() => watchedTools || [], [watchedTools]);
  const selectedIds = new Set(selectedTools.map((t) => t.tool_id));

  // Same placement source of truth the Modules tab uses (toolPlacement.ts) — core_tools from the
  // same /agents/screens endpoint, plus this agent's tool_scope_overrides.
  const { data: screens } = useListScreensQuery();
  const watchedOverrides = watch('tool_scope_overrides');
  const overrides = useMemo(
    () => ({ global_tools: watchedOverrides?.global_tools ?? [], screens: watchedOverrides?.screens ?? {}, core_opt_out: watchedOverrides?.core_opt_out ?? [] }),
    [watchedOverrides],
  );
  const ownedNames = useMemo(
    () => selectedTools.map((t) => t.name || t.tool_name).filter(Boolean) as string[],
    [selectedTools],
  );
  const blockToolSources = useWatch({ control, name: 'system_prompts' });
  const toolPlacements = useMemo(
    () => computeToolPlacements(ownedNames, overrides, screens?.core_tools ?? [], blockToolSources ?? []),
    [ownedNames, overrides, screens, blockToolSources],
  );

  // Display-only: sort for a deterministic A-Z list, same order wherever this list is shown.
  // Removal/update/expand all key off tool_id, so this never touches the stored `tools` order.
  const sortedSelectedTools = useMemo(
    () => [...selectedTools].sort((a, b) =>
      (a.name || a.tool_name || '').localeCompare(b.name || b.tool_name || '')),
    [selectedTools],
  );

  const [expandedId, setExpandedId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  const toggleExpand = (id: string | undefined) =>
    setExpandedId((prev) => (prev === id ? undefined : id));

  const addTool = (t: AgentTool) => {
    setValue(
      'tools',
      [
        ...selectedTools,
        {
          tool_id: t.tool_id,
          name: t.name,
          tool_name: t.tool_name || t.name,
          description: t.description,
          requires_confirmation: t.requires_confirmation ?? false,
          argument_schema: t.argument_schema,
          toolkit_type: t.toolkit_type,
          toolkit_name: t.toolkit_name,
          action_card_template: t.action_card_template,
        },
      ],
      { shouldDirty: true }
    );
  };

  const removeTool = (toolId: string | undefined) => {
    const removed = selectedTools.find((t) => t.tool_id === toolId);
    setValue(
      'tools',
      selectedTools.filter((t) => t.tool_id !== toolId),
      { shouldDirty: true }
    );
    // Drop the tool from any Screens-tab placement too, so re-attaching it later starts clean
    // instead of silently restoring an old screen assignment.
    const name = removed?.name || removed?.tool_name;
    const ov = getValues('tool_scope_overrides');
    if (name && ov) {
      const screens = Object.fromEntries(
        Object.entries(ov.screens ?? {})
          .map(([m, names]) => [m, names.filter((n) => n !== name)] as const)
          .filter(([, names]) => names.length > 0),
      );
      setValue(
        'tool_scope_overrides',
        { global_tools: (ov.global_tools ?? []).filter((n) => n !== name), screens, core_opt_out: (ov.core_opt_out ?? []).filter((n) => n !== name) },
        { shouldDirty: true },
      );
    }
    if (expandedId === toolId) setExpandedId(undefined);
  };

  const updateTool = (toolId: string | undefined, patch: Partial<AgentTool>) =>
    setValue(
      'tools',
      selectedTools.map((t) => (t.tool_id === toolId ? { ...t, ...patch } : t)),
      { shouldDirty: true }
    );

  // group catalogue tools by toolkit_name (ungrouped → "Other")
  const toolkitGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? allTools.filter(
          (t) =>
            (t.name || t.tool_name || '').toLowerCase().includes(q) ||
            (t.description || '').toLowerCase().includes(q) ||
            (t.toolkit_name || '').toLowerCase().includes(q)
        )
      : allTools;

    const map = new Map<string, AgentTool[]>();
    for (const t of filtered) {
      const key = t.toolkit_name || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    // sort: named toolkits first, "Other" last
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
  }, [allTools, search]);

  return (
    <>
      {/* ── Enabled tools ── */}
      <SectionCard
        title="Enabled tools"
        desc="Click a tool to edit its name, description, schema, and confirmation behaviour."
      >
        {selectedTools.length === 0 ? (
          <EmptyState variant="inline" title="No tools enabled yet. Add one from the catalogue below." />
        ) : (
          <div className="space-y-2">
            {sortedSelectedTools.map((t) => (
              <EnabledToolRow
                key={t.tool_id}
                tool={t}
                placement={toolPlacements.get(t.name || t.tool_name || '')}
                expanded={expandedId === t.tool_id}
                onToggleExpand={() => toggleExpand(t.tool_id)}
                onRemove={() => removeTool(t.tool_id)}
                onUpdate={(patch) => updateTool(t.tool_id, patch)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* ── Tool catalogue ── */}
      <SectionCard
        title="Tool catalogue"
        desc="Tools are grouped by toolkit. Expand a toolkit to add tools to this agent, or expand a tool to edit its shared output schema and return-shaping behavior."
      >
        {/* search */}
        <div className="mb-3">
          <input
            className={inputCls}
            placeholder="Search tools or toolkits…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {toolkitGroups.length === 0 ? (
          <EmptyState variant="inline" title="No tools match your search." />
        ) : (
          <div className="space-y-2">
            {toolkitGroups.map(([name, tools]) => (
              <ToolkitGroup
                key={name}
                name={name}
                tools={tools}
                enabledIds={selectedIds}
                onAdd={addTool}
                forceOpen={search.trim().length > 0}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default ToolsTab;
